import * as ed from "@noble/ed25519";
import { sha256 } from "@noble/hashes/sha256";
import { SignatureError } from "./errors.js";
import type { Verdict, PublicVerdict } from "./types.js";

/**
 * Canonicalize a verdict to a deterministic byte string for signing.
 * Sort keys alphabetically and use stable JSON.
 */
function canonicalize(verdict: Verdict): Uint8Array {
  const sorted = JSON.stringify(verdict, Object.keys(verdict).sort());
  return new TextEncoder().encode(sorted);
}

/**
 * Compute a commitment hash that anchors the verdict to the target wallet.
 * This is published alongside the signature so verifiers can check that the
 * verdict was computed against the claimed wallet and not substituted later.
 */
export function commitmentHash(verdict: Verdict): string {
  const payload = `${verdict.target.wallet}|${verdict.target.handle}|${verdict.aggregate_score}|${verdict.scanned_at}`;
  const digest = sha256(new TextEncoder().encode(payload));
  return Buffer.from(digest).toString("hex");
}

/**
 * Sign a verdict with an ed25519 private key. Returns a `PublicVerdict`
 * ready to publish on the feed.
 */
export async function signVerdict(
  verdict: Verdict,
  privateKeyHex: string,
  feedUrl: string
): Promise<PublicVerdict> {
  const priv = Buffer.from(privateKeyHex, "hex");
  if (priv.length !== 32) {
    throw new SignatureError(
      `ed25519 private key must be 32 bytes, got ${priv.length}`
    );
  }

  const message = canonicalize(verdict);
  const signature = await ed.signAsync(message, priv);

  return {
    ...verdict,
    signature: Buffer.from(signature).toString("hex"),
    commitment: commitmentHash(verdict),
    posted_at: Math.floor(Date.now() / 1000),
    feed_url: feedUrl,
  };
}

/**
 * Verify a public verdict was signed by the given ed25519 public key and
 * that its commitment hash still matches the claimed target.
 */
export async function verifyVerdict(
  publicVerdict: PublicVerdict,
  publicKeyHex: string
): Promise<boolean> {
  const pub = Buffer.from(publicKeyHex, "hex");
  if (pub.length !== 32) {
    throw new SignatureError(
      `ed25519 public key must be 32 bytes, got ${pub.length}`
    );
  }

  const { signature, commitment, posted_at, feed_url, ...verdictOnly } =
    publicVerdict;

  const expectedCommitment = commitmentHash(verdictOnly as Verdict);
  if (expectedCommitment !== commitment) {
    throw new SignatureError(
      `commitment mismatch: ${expectedCommitment} vs ${commitment}`
    );
  }

  const message = canonicalize(verdictOnly as Verdict);
  const sig = Buffer.from(signature, "hex");

  return ed.verifyAsync(sig, message, pub);
}
